import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "./routes";

function roleBadgeLabel(roleName) {
  return String(roleName || "Admin")
    .trim()
    .toUpperCase();
}

export default function AdminAccountMenu({
  profileImageSrc,
  displayName,
  userEmail,
  workspaceLabel,
  roleName,
  canEditBranding,
  showSwitchWorkspace,
  onLogout,
  dark,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  const itemClass = dark
    ? "block w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-700/80 rounded-lg transition-colors"
    : "block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className={`admin-topnav-avatar-btn rounded-full ring-2 ring-transparent focus:outline-none focus-visible:ring-[#004f73]/40 ${
          open ? "ring-[#004f73]/30" : ""
        }`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        <img
          src={profileImageSrc}
          alt=""
          className={`admin-topnav-avatar ${dark ? "admin-topnav-avatar--dark" : "admin-topnav-avatar--light"}`}
        />
      </button>
      {open ? (
        <div
          role="menu"
          className={`admin-account-menu absolute right-0 top-[calc(100%+0.5rem)] z-50 min-w-[16.5rem] max-w-[19rem] rounded-xl border shadow-lg py-2 px-1.5 ${
            dark
              ? "border-slate-600 bg-slate-800 text-slate-100"
              : "border-slate-200 bg-white text-slate-900"
          }`}
        >
          <div className="admin-account-menu-head px-3 py-2.5 border-b border-slate-200/80 dark:border-slate-600/80 mb-1">
            <div className="flex items-start gap-2.5">
              <img src={profileImageSrc} alt="" className="admin-account-menu-avatar shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-50 truncate leading-tight">
                  {displayName}
                </p>
                {userEmail ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{userEmail}</p>
                ) : null}
                {workspaceLabel ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-1">{workspaceLabel}</p>
                ) : null}
                {roleName ? (
                  <span className="admin-account-role-badge mt-2">{roleBadgeLabel(roleName)}</span>
                ) : null}
              </div>
            </div>
          </div>
          {canEditBranding ? (
            <Link role="menuitem" to={ROUTES.branding} className={itemClass} onClick={close}>
              Workspace branding
            </Link>
          ) : null}
          {showSwitchWorkspace ? (
            <Link role="menuitem" to={ROUTES.switchWorkspace} className={itemClass} onClick={close}>
              Switch organisation
            </Link>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className={`${itemClass} text-red-600 dark:text-red-400`}
            onClick={() => {
              close();
              onLogout();
            }}
          >
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}
